// @MX:ANCHOR: [AUTO] LotStatus 현황판 API 모듈 — china-inapi GET /lot-status/dashboard
// @MX:REASON: [AUTO] LotFlowDashboard + 사이드바 precheck 모두 이 모듈을 통해 china-inapi 호출. SPEC-CHINA-LOT-FLOW-DASHBOARD-001 REQ-BE-DASH-001
// @MX:SPEC: SPEC-CHINA-LOT-FLOW-DASHBOARD-001
// @MX:NOTE: [AUTO] BE 신규 query param 추가 시 반드시 getDashboard wrapper 갱신 필수.
//           단편 편집으로 wrapper 갱신 누락 시 필터 동작 안 함 (2026-06-11 lotNumber + quantityBalance 사례).
//           SPEC-CHINA-LOT-FLOW-FILTER-EXPAND-001: minSalesCustomerCount, minDealerCurrentInventoryQty 추가 (2026-06-12)
//           SPEC-CHINA-LOT-FLOW-FILTER-DATE-AGING-001: 날짜 범위 2축 + aging 5 param 추가 (2026-06-15)
//           LOT-FLOW-FILTER-UX-FE: minSalesAmount, maxSalesAmount 추가 (2026-06-15)

import chinaInapi from './chinaInapi';

/**
 * LotStatus 현황판 API — REQ-BE-DASH-001~014
 * GET /lot-status/dashboard
 *
 * 응답 shape: §3.2
 *   kpi, rows, pagination, appliedFilters
 *
 * query 파라미터 전체 목록 (BE 추가 시 이 wrapper도 반드시 동기화):
 *   search, lotNumber, flowStage, quantityBalance, anomalyOnly,
 *   salesCustomerCodes, minSalesCustomerCount, minDealerCurrentInventoryQty,
 *   ecountShippedDateFrom, ecountShippedDateTo, dealerSoldDateFrom, dealerSoldDateTo,
 *   minEcountShippedAgingDays,
 *   minSalesAmount, maxSalesAmount,
 *   sortBy, sortOrder, page, pageSize
 */
const lotStatusApi = {
  /**
   * 현황판 데이터 조회 — GTIN 그룹 집계 + KPI 5종 + 페이지네이션
   * @param {Object} query - 위 파라미터 전체 목록 참조
   * @returns {Promise} 응답 스키마 §3.2
   */
  getDashboard(query = {}) {
    const params = {};
    // 검색 파라미터
    // search: GTIN exact / productDescription prefix
    if (query.search) params.search = query.search;
    // lotNumber: prefix 일치 검색 (예: BEAZ → BEAZ로 시작하는 모든 lot)
    if (query.lotNumber) params.lotNumber = query.lotNumber;
    // 필터 파라미터
    if (query.flowStage) params.flowStage = query.flowStage;
    // quantityBalance: 'ok' | 'mismatch' (FE에서 2개 옵션으로 단순화, BE는 ok/warning/error/mismatch 지원)
    if (query.quantityBalance) params.quantityBalance = query.quantityBalance;
    if (query.anomalyOnly) params.anomalyOnly = 'true';
    if (query.salesCustomerCodes) params.salesCustomerCodes = query.salesCustomerCodes;
    // 확장 필터 (SPEC-CHINA-LOT-FLOW-FILTER-EXPAND-001)
    if (query.minSalesCustomerCount) params.minSalesCustomerCount = query.minSalesCustomerCount;
    if (query.minDealerCurrentInventoryQty) params.minDealerCurrentInventoryQty = query.minDealerCurrentInventoryQty;
    // 날짜/aging 필터 (SPEC-CHINA-LOT-FLOW-FILTER-DATE-AGING-001) — [CRITICAL] wrapper 갱신 필수
    if (query.ecountShippedDateFrom) params.ecountShippedDateFrom = query.ecountShippedDateFrom;
    if (query.ecountShippedDateTo) params.ecountShippedDateTo = query.ecountShippedDateTo;
    if (query.dealerSoldDateFrom) params.dealerSoldDateFrom = query.dealerSoldDateFrom;
    if (query.dealerSoldDateTo) params.dealerSoldDateTo = query.dealerSoldDateTo;
    if (query.minEcountShippedAgingDays) params.minEcountShippedAgingDays = query.minEcountShippedAgingDays;
    // 금액 필터 (LOT-FLOW-FILTER-UX-FE) — BE 추가 중, 준비됨
    if (query.minSalesAmount != null && query.minSalesAmount !== '') params.minSalesAmount = query.minSalesAmount;
    if (query.maxSalesAmount != null && query.maxSalesAmount !== '') params.maxSalesAmount = query.maxSalesAmount;
    // 정렬 + 페이지네이션
    if (query.sortBy) params.sortBy = query.sortBy;
    if (query.sortOrder) params.sortOrder = query.sortOrder;
    if (query.page) params.page = query.page;
    if (query.pageSize) params.pageSize = query.pageSize;
    return chinaInapi.get('/lot-status/dashboard', { params });
  },

  /**
   * BE endpoint 가용성 precheck — REQ-FE-DASH-002a
   * HEAD /lot-status/dashboard — body 없이 status code만 확인
   * @returns {Promise}
   */
  precheckDashboard() {
    return chinaInapi.head('/lot-status/dashboard');
  },

  /**
   * 출고처 distinct 목록 조회 — 흐름 현황판 출고처 필터 옵션용
   * GET /lot-status/dashboard/sales-customers
   * @returns {Promise} data: [{ customerCode, customerName, salesCount }]
   */
  getSalesCustomers() {
    return chinaInapi.get('/lot-status/dashboard/sales-customers');
  },

  /**
   * lot별 거래상세 라인 목록 조회 — SPEC-CHINA-LOT-FLOW-SALES-DETAIL-001 REQ-FE-EXP-001
   * GET /lot-status/dashboard/:lotNumber/sales-lines
   * @param {string} lotNumber — 조회할 lotNumber
   * @returns {Promise} { lotNumber, lineCount, data: LotSalesLineEntry[] }
   */
  getLotSalesLines(lotNumber) {
    return chinaInapi.get(`/lot-status/dashboard/${encodeURIComponent(lotNumber)}/sales-lines`);
  },

  // @MX:ANCHOR: [AUTO] reconcilePreview — 딜러 배치 단건 대조 미리보기 (dry-run)
  // @MX:REASON: [AUTO] LotFlowDashboard ReconcilePreviewModal 에서만 호출. GSP 재조회 + diff 산출, 쓰기 0건. 응답에 overallDiagnosis/quantityBalance 포함.
  /**
   * 딜러 배치 단건 보정 미리보기 — POST /dealer-gsp/reconcile/preview
   * dry-run: 쓰기 없음. 응답에 perType[].diagnosis + overallDiagnosis + quantityBalance(current/projected) 포함.
   * @param {{ batchNumber: string, types?: string[], window?: {begin:string,end:string} }} body
   * @returns {Promise}
   */
  reconcilePreview(body) {
    // @MX:WARN: [AUTO] timeout 60s 초과 가능 (GSP IF1001 per-day 청킹, window 길면 수분 소요)
    // @MX:REASON: [AUTO] 설계 §4.2: reconcileTimeoutMs=600,000ms. chinaInapi 기본 60s 이내인 경우 proxy 503 가능하나 BE 쪽 처리는 계속됨. 운영자에게 재조회 안내 필요.
    return chinaInapi.post('/dealer-gsp/reconcile/preview', body, { timeout: 600000 });
  },

  /**
   * 딜러 배치 단건 보정 실행 — POST /dealer-gsp/reconcile/apply
   * @param {{ batchNumber: string, reason: string, types?: string[], window?: {begin:string,end:string} }} body
   * @returns {Promise}
   */
  reconcileApply(body) {
    return chinaInapi.post('/dealer-gsp/reconcile/apply', body, { timeout: 600000 });
  },
};

export default lotStatusApi;
